export interface PlaceHoldInfoImpl {
  message: string;
  guardiansBlocking: string[];
  success: boolean;
  bibliographicId: string;
  pickupBranchId: string;
  titleLevelHold: boolean;
  interfaceType: "PAC" | "KIDS";
  downloadable: boolean;
  serialItem: boolean;
}
export interface HostSystemDiagImpl {
  methodSupported: boolean;
  validHostUser: boolean;
  validSessionUser: boolean;
  hostSystemFailure: boolean;
}
export interface PlaceHoldReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  placeHoldInfos: PlaceHoldInfoImpl[];
}
export interface CancelHoldInfoImpl {
  desireNumber: number;
  whichBranch: number;
  bibliographicId: string;
  holdQueueLength: number;
  success: boolean;
  downloadable: boolean;
  cancelable: boolean;
  status: string;
  messageToken: string;
}
export interface CancelHoldReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  cancelHoldInfos: CancelHoldInfoImpl[];
}
export interface SuspendHoldInfo {
  desireNumber: number;
  success: boolean;
  suspendDate: Date;
  queuePosition: number;
  bibliographicId: string;
  pickupBranchId: string;
  downloadable: boolean;
  suspendable: boolean;
}
export interface SuspendHoldReturn {
  hostSystemDiag: HostSystemDiagImpl;
  suspendHoldInfos: SuspendHoldInfo[];
}
import axios from "axios";
import { ResourceDWR } from "../../dto/SuggestionResult";

export const placeHoldRequest = async (
  placeHoldInfos: PlaceHoldInfoImpl[],
  limitTitleLevelHoldsToPatronBranch = false
): Promise<PlaceHoldReturnImpl> => {
  const response = await axios.post<PlaceHoldReturnImpl>(
    `/requests/${limitTitleLevelHoldsToPatronBranch}`,
    {
      placeHoldInfos: JSON.stringify(placeHoldInfos),
    }
  );
  return response.data;
};

export const cancelHold = async (
  holds: HostSystemItemView[]
): Promise<CancelHoldReturnImpl> => {
  const cancelHoldInfos: Omit<
    CancelHoldInfoImpl,
    "cancelable" | "messageToken"
  >[] = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      status,
      downloadable,
    }) => {
      return {
        desireNumber: parseInt(holdNumber),
        success: false,
        holdQueueLength: parseInt(holdQueueLength),
        bibliographicId: bibliographicId,
        whichBranch: holdPickupBranchId,
        status: status,
        downloadable: downloadable,
      };
    }
  );
  const response = await axios.post<CancelHoldReturnImpl>("/requests/cancel", {
    cancelHoldInfos: JSON.stringify(cancelHoldInfos),
  });
  return response.data;
};

export const suspendHold = async (
  holds: HostSystemItemView[],
  suspendUntil: Date
): Promise<SuspendHoldReturn> => {
  // TODO confirm discrepencies in property types for SuspendHoldInfo and HostSystemItemView
  // (parseInt and toString below)

  const suspendHoldInfos = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      downloadable,
    }) => {
      const holdInfo: Omit<SuspendHoldInfo, "suspendable"> = {
        desireNumber: parseInt(holdNumber),
        success: false,
        suspendDate: suspendUntil,
        queuePosition: parseInt(holdQueueLength),
        bibliographicId,
        pickupBranchId: holdPickupBranchId.toString(),
        downloadable: downloadable,
      };
      return holdInfo;
    }
  );
  const response = await axios.post<SuspendHoldReturn>("/requests/suspend", {
    suspendHoldInfos: JSON.stringify(suspendHoldInfos),
  });
  return response.data;
};

export const unsuspendHold = async (
  holds: HostSystemItemView[]
): Promise<SuspendHoldReturn> => {
  const suspendHoldInfos = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      downloadable,
    }) => {
      const holdInfo: Omit<SuspendHoldInfo, "suspendable"> = {
        desireNumber: parseInt(holdNumber),
        success: false,
        suspendDate: null,
        queuePosition: parseInt(holdQueueLength),
        bibliographicId,
        pickupBranchId: holdPickupBranchId.toString(),
        downloadable: downloadable,
      };
      return holdInfo;
    }
  );

  const response = await axios.post<SuspendHoldReturn>("/requests/unsuspend", {
    suspendHoldInfos: JSON.stringify(suspendHoldInfos),
  });
  return response.data;
};

export enum HostSystemItemSortType {
  OutDate = "OutDate",
  DueDate = "DueDate",
  Author = "Author",
  QueuePosition = "QueuePosition",
  Status = "Status",
  Title = "Title",
}
export interface GetAllHoldsReturn {
  holds: HostSystemItemView[];
  // Add any other relevant properties here based on your needs
}

export interface HostSystemItemView extends HostSystemItem {
  itemId: string;
  resource: ResourceDWR;
}

export interface EResourceFormat {
  formatId: string;
  formatToken: string;
  availableOn: Date;
  preRelease: boolean;
}

export interface HostSystemItem {
  id: string;
  title: string;
  titleSortKey: string;
  author: string;
  authorSortKey: string;
  chronology: string;
  chronologySortKey: string;
  enumeration: string;
  enumerationSortKey: string;
  holdPickupDate: string;
  holdPickupBranch: string;
  holdQueueLength: string;
  holdQueueLengthAsLong: number | null;
  holdPickupBy: Date | null;
  bibliographicId: string;
  dueDate: Date | null;
  outDate: Date | null;
  message: string;
  fee: number | null;
  holdNumber: string;
  holdRequestedDate: Date | null;
  holdCancelable: boolean;
  loanCancelable: boolean;
  status: string;
  suspendUntilDate: Date | null;
  holdPickupBranchId: number | null;
  chargeNumber: string;
  downloadable: boolean;
  pushable: boolean;
  holdSuspendable: boolean;
  eResourceUrl: string;
  useRedirectForURL: boolean;
  eResourceId: string;
  formatInfo: EResourceFormat[];
  renewable: boolean;
  renewalCount: number;
  renewalLimit: number | null;
  sortStatus: string; // Derived from the `getSortStatus()` method
  holdPickupByString: string; // Derived from the `getHoldPickupByString()` method
  dueDateString: string; // Derived from the `getDueDateString()` method
  outDateString: string; // Derived from the `getOutDateString()` method
  holdRequestedDateString: string; // Derived from the `getHoldRequestedDateString()` method
  suspendUntilDateString: string; // Derived from the `getSuspendUntilDateString()` method
  feeComment: string; // Derived from the `getFeeComment()` method
  eResourceService: string; // Derived from the `getEResourceService()` method
}

export const getAllHoldsSortable = async (
  page: number,
  count: number,
  sortType: HostSystemItemSortType,
  includeResources = true
): Promise<GetAllHoldsReturn> => {
  const start = page * count;
  const response = await axios.get<GetAllHoldsReturn>(
    `/requests/${start}/${count}/${sortType}/${includeResources}`
  );
  return response.data;
};
